/**
 * Alshallal HR — Document Expiry helpers (single source of truth).
 *
 * Why this exists:
 *   `new Date("2026-12-31")` is parsed as UTC midnight, then compared against
 *   `new Date()` which is *now* in local time. In Saudi (UTC+3) at 9 AM on
 *   2026-12-31 the document's parsed expiry (00:00 UTC) is already 6 h in
 *   the past, so the doc is wrongly flagged as expired. Multiple pages had
 *   their own buggy variant of this — this module is the only correct way.
 *
 * Rules:
 *   - A document is considered EXPIRED only when the calendar day in the user's
 *     locale is STRICTLY AFTER the expiry date.
 *   - "Expiring soon" means days_until_expiry ∈ [0, 30].
 *   - When the backend has already calculated `is_expired` /
 *     `days_until_expiry` on the document, prefer those values verbatim — the
 *     backend uses `date.today()` (date-only, no timezone gotcha).
 */

function _todayYMD() {
  const t = new Date();
  return t.getFullYear() * 10000 + (t.getMonth() + 1) * 100 + t.getDate();
}

function _ymdFromString(s) {
  if (typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return y * 10000 + mo * 100 + d;
}

/**
 * Returns true if the document/expiry-date is past expiry.
 * Accepts either:
 *   - a document object (will prefer `is_expired` / `expiry_date` fields), or
 *   - a raw YYYY-MM-DD string.
 */
export function isExpired(docOrDate) {
  if (docOrDate == null) return false;

  // Document object: trust backend-computed field if present
  if (typeof docOrDate === 'object') {
    if (typeof docOrDate.is_expired === 'boolean') return docOrDate.is_expired;
    if (typeof docOrDate.days_until_expiry === 'number') return docOrDate.days_until_expiry < 0;
    return isExpired(docOrDate.expiry_date);
  }

  const ymd = _ymdFromString(docOrDate);
  if (!ymd) return false;
  return ymd < _todayYMD();
}

/**
 * Returns true if the document is within `withinDays` of expiry AND not yet
 * expired. Defaults to 30 days.
 */
export function isExpiringSoon(docOrDate, withinDays = 30) {
  if (docOrDate == null) return false;

  if (typeof docOrDate === 'object') {
    if (typeof docOrDate.days_until_expiry === 'number') {
      return docOrDate.days_until_expiry >= 0 && docOrDate.days_until_expiry <= withinDays;
    }
    if (docOrDate.is_expired === true) return false;
    return isExpiringSoon(docOrDate.expiry_date, withinDays);
  }

  const expiryYMD = _ymdFromString(docOrDate);
  if (!expiryYMD) return false;
  const todayYMD = _todayYMD();
  if (expiryYMD < todayYMD) return false;
  const days = daysUntilExpiry(docOrDate);
  return days !== null && days >= 0 && days <= withinDays;
}

/**
 * Whole-day count from today to the expiry date (negative if past).
 * Returns null when the input is missing or unparsable.
 */
export function daysUntilExpiry(docOrDate) {
  if (docOrDate == null) return null;

  if (typeof docOrDate === 'object') {
    if (typeof docOrDate.days_until_expiry === 'number') return docOrDate.days_until_expiry;
    return daysUntilExpiry(docOrDate.expiry_date);
  }

  const ymd = _ymdFromString(docOrDate);
  if (!ymd) return null;
  const y = Math.floor(ymd / 10000);
  const m = Math.floor(ymd / 100) % 100;
  const d = ymd % 100;
  const expiryDay = new Date(y, m - 1, d).getTime();
  const t = new Date();
  const today = new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
  return Math.round((expiryDay - today) / 86400000);
}

/** Format YYYY-MM-DD as local-calendar date string (no timezone shift). */
export function formatExpiryDate(s, locale) {
  if (!s || typeof s !== 'string') return '—';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return s;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  try {
    return d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return s;
  }
}
